export default class LookInspirations extends HTMLElement {
  private list: HTMLUListElement | null = null;
  private items: HTMLLIElement[] = [];
  private prevButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;

  private currentIndex = 0;

  private onScrollBound = this.onScroll.bind(this);
  private onResizeBound = this.onResize.bind(this);
  private onPrevButtonClickBound = this.onPrevButtonClick.bind(this);
  private onNextButtonClickBound = this.onNextButtonClick.bind(this);

  connectedCallback(): void {
    this.list = this.querySelector('[data-look-inspirations-ref="list"]');
    this.items = Array.from(this.querySelectorAll('[data-look-inspirations-ref="item"]'));
    this.prevButton = this.querySelector('[data-look-inspirations-ref="prev-button"]');
    this.nextButton = this.querySelector('[data-look-inspirations-ref="next-button"]');

    if (!this.list || this.items.length === 0) return;

    this.addEventListeners();

    const initialIndex = Math.floor(this.items.length / 2);
    this.scrollToItem(initialIndex, false);
    this.currentIndex = initialIndex;
    this.updateButtons();
  }

  addEventListeners(): void {
    this.list?.addEventListener('scroll', this.onScrollBound, { passive: true });
    window.addEventListener('resize', this.onResizeBound);
    this.prevButton?.addEventListener('click', this.onPrevButtonClickBound);
    this.nextButton?.addEventListener('click', this.onNextButtonClickBound);
  }

  removeEventListeners(): void {
    this.list?.removeEventListener('scroll', this.onScrollBound);
    window.removeEventListener('resize', this.onResizeBound);
    this.prevButton?.removeEventListener('click', this.onPrevButtonClickBound);
    this.nextButton?.removeEventListener('click', this.onNextButtonClickBound);
  }

  private getClosestItemIndex(): number {
    if (!this.list || this.items.length === 0) return 0;

    const listCenter = this.list.scrollLeft + this.list.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.items.forEach((item, index) => {
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const distance = Math.abs(itemCenter - listCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  private getScrollLeftForItem(item: HTMLLIElement): number {
    if (!this.list) return 0;

    return item.offsetLeft - (this.list.clientWidth - item.offsetWidth) / 2;
  }

  private canScrollPrev(): boolean {
    return this.currentIndex > 0;
  }

  private canScrollNext(): boolean {
    return this.currentIndex < this.items.length - 1;
  }

  updateButtons(): void {
    const canScrollPrev = this.canScrollPrev();
    const canScrollNext = this.canScrollNext();

    this.prevButton?.classList.toggle('opacity-50', !canScrollPrev);
    this.prevButton?.classList.toggle('cursor-pointer', canScrollPrev);
    if (!canScrollPrev) {
      this.prevButton?.setAttribute('disabled', 'true');
    } else {
      this.prevButton?.removeAttribute('disabled');
    }
    this.prevButton?.setAttribute('aria-disabled', !canScrollPrev ? 'true' : 'false');

    this.nextButton?.classList.toggle('opacity-50', !canScrollNext);
    this.nextButton?.classList.toggle('cursor-pointer', canScrollNext);
    if (!canScrollNext) {
      this.nextButton?.setAttribute('disabled', 'true');
    } else {
      this.nextButton?.removeAttribute('disabled');
    }
    this.nextButton?.setAttribute('aria-disabled', !canScrollNext ? 'true' : 'false');
  }

  scrollToItem(index: number, animate: boolean): void {
    if (!this.list) return;

    const item = this.items[index];
    if (!item) return;

    this.list.scrollTo({
      left: this.getScrollLeftForItem(item),
      behavior: animate ? 'smooth' : 'instant',
    });

    this.currentIndex = index;
  }

  onPrevButtonClick(): void {
    if (!this.canScrollPrev()) return;

    this.scrollToItem(this.currentIndex - 1, true);
  }

  onNextButtonClick(): void {
    if (!this.canScrollNext()) return;

    this.scrollToItem(this.currentIndex + 1, true);
  }

  onScroll(): void {
    this.currentIndex = this.getClosestItemIndex();
    this.updateButtons();
  }

  onResize(): void {
    this.scrollToItem(this.currentIndex, false);
    this.updateButtons();
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }
}

customElements.define('look-inspirations', LookInspirations);
